import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CreditCard, Shield, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign_id');
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (campaignId) {
      fetchCampaign();
    }
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaign(response.data);
    } catch (error) {
      toast.error('Kampagne nicht gefunden');
      navigate('/dashboard');
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API}/payments/checkout-session`,
        { 
          campaign_id: campaignId,
          origin_url: originUrl
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Zahlung');
      setLoading(false);
    }
  };

  if (!campaign) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  // Calculate pricing
  const budget = campaign.budget || 0;
  const managementFee = budget * 0.20;
  const expertOptimizationFee = campaign.expert_optimization ? (campaign.expert_optimization_fee || 29) : 0;
  const totalAmount = budget + managementFee + expertOptimizationFee;

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-gray-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-4"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zum Dashboard
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="checkout-title">Zahlung abschließen</h1>
          <p className="text-gray-600 mb-8">Sichern Sie sich Ihre Google Suchanzeige</p>

          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-gray-900 mb-4">Suchanzeigen-Übersicht</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Unternehmen:</span>
                <span className="font-medium" data-testid="checkout-company-name">{campaign.company_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Standort:</span>
                <span className="font-medium">{campaign.location}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Werbebudget:</span>
                <span className="font-medium">{budget.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Laufzeit:</span>
                <span className="font-medium">{campaign.laufzeit === 'monatlich' ? 'Monatlich' : 'Einmalig'}</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 space-y-3 mb-8">
            <div className="flex justify-between text-lg">
              <span className="text-gray-700">Werbebudget:</span>
              <span className="font-semibold">{budget.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-lg">
              <span className="text-gray-700">Management-Fee (20%):</span>
              <span className="font-semibold">{managementFee.toFixed(2)} €</span>
            </div>
            
            {campaign.expert_optimization && (
              <div className="flex justify-between text-lg py-3 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700 font-medium">Experten-Optimierung:</span>
                </div>
                <span className="font-semibold text-blue-600">{expertOptimizationFee.toFixed(2)} €</span>
              </div>
            )}
            
            <div className="border-t-2 border-gray-200 pt-3 flex justify-between text-2xl font-bold">
              <span>Gesamtbetrag:</span>
              <span className="text-blue-600" data-testid="checkout-price">{totalAmount.toFixed(2)} €</span>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {campaign.laufzeit === 'monatlich' ? '* Monatlich abgerechnet' : '* Einmalige Zahlung'}
              {campaign.expert_optimization && <div className="mt-1 text-blue-600">* Inkl. einmaliger Experten-Optimierung</div>}
            </div>
          </div>

          <div className="flex items-center justify-center mb-6 text-sm text-gray-500">
            <Shield className="w-4 h-4 mr-2" />
            Sichere Zahlung mit Stripe
          </div>

          <Button
            onClick={handlePayment}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
            data-testid="proceed-to-payment-btn"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            {loading ? 'Wird weitergeleitet...' : `Jetzt bezahlen ${totalAmount.toFixed(2)} €`}
          </Button>

          <p className="text-xs text-gray-500 text-center mt-4">
            Nach der Zahlung wird Ihre Kampagne von uns geprüft und aktiviert.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Checkout;